import type { ReactNode } from 'react'
import { EmptyState } from './EmptyState'

export interface Column<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  /** Right-align numbers so decimal points line up down the column. */
  numeric?: boolean
  width?: string
}

export interface DataTableProps<T> {
  rows: readonly T[]
  columns: ReadonlyArray<Column<T>>
  rowKey: (row: T) => string
  /** Applied to voided rows so a correction is visible rather than hidden. */
  rowClassName?: (row: T) => string | undefined
  empty?: ReactNode
  caption?: string
}

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  rowClassName,
  empty,
  caption,
}: DataTableProps<T>) {
  if (rows.length === 0) {
    return <>{empty ?? <EmptyState title="Nothing recorded yet." />}</>
  }

  return (
    <div className="table-scroll">
      <table className="data-table">
        {caption ? <caption>{caption}</caption> : null}
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                className={column.numeric ? 'numeric' : undefined}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className={rowClassName?.(row)}>
              {columns.map((column) => (
                <td key={column.key} className={column.numeric ? 'numeric' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
